import { AlbedoModule } from '@creit.tech/stellar-wallets-kit/modules/albedo';
import { FREIGHTER_ID, FreighterModule } from '@creit.tech/stellar-wallets-kit/modules/freighter';
import { HanaModule } from '@creit.tech/stellar-wallets-kit/modules/hana';
import { HotWalletModule } from '@creit.tech/stellar-wallets-kit/modules/hotwallet';
import { METAMASK_ID, MetaMaskModule } from '@creit.tech/stellar-wallets-kit/modules/metamask';
import { RabetModule } from '@creit.tech/stellar-wallets-kit/modules/rabet';
import { xBullModule } from '@creit.tech/stellar-wallets-kit/modules/xbull';
import { parseError, StellarWalletsKit } from '@creit.tech/stellar-wallets-kit/sdk';
import { activeAddress, resetWalletState } from '@creit.tech/stellar-wallets-kit/state';
import { ModuleInterface, Networks } from '@creit.tech/stellar-wallets-kit/types';
import { WatchWalletChanges } from '@stellar/freighter-api';
import * as StellarSdk from '@stellar/stellar-sdk';
import { TransactionBuilder } from '@stellar/stellar-sdk';

import { getNetworkPassphrase } from 'helpers/env';

import { AuthEvent, AuthPayload } from 'services/auth/events/events';
import EventService from 'services/event.service';
import { ModalService, ToastService } from 'services/globalServices';

import ChooseLoginMethodModal from 'modals/auth/ChooseLoginMethodModal';
import WalletKitModal from 'modals/WalletKitModal';

export default class WalletKitServiceClass {
    private readonly event: EventService<AuthEvent, AuthPayload>;
    private connectedWalletId: string | null = null;
    private connectedAddress: string | null = null;
    private loginPending = false;

    readonly modules: ModuleInterface[];
    watcher: WatchWalletChanges | null = null;

    constructor(event: EventService<AuthEvent, AuthPayload>) {
        this.event = event;

        const metamask = getNetworkPassphrase() === Networks.PUBLIC ? new MetaMaskModule() : null;
        this.modules = [
            new FreighterModule(),
            ...(metamask ? [metamask] : []),
            new HotWalletModule(),
            new xBullModule(),
            new AlbedoModule(),
            new HanaModule(),
            new RabetModule(),
        ];

        StellarWalletsKit.init({
            network: getNetworkPassphrase() as Networks,
            modules: this.modules,
        });

        // Aquarius owns the saved login, including sessions created with Wallets Kit v1.
        resetWalletState();

        metamask?.onChange(({ address, error }) => {
            if (
                this.connectedWalletId !== METAMASK_ID ||
                error ||
                !address ||
                address === this.connectedAddress
            ) {
                return;
            }
            this.clearSession();
            this.event.trigger({ type: AuthEvent.walletKitLogout });
            ToastService.showErrorToast(
                'Account changed in MetaMask. Please reconnect your wallet.',
            );
        });
        metamask?.adapter.on('disconnect', () => {
            if (this.connectedWalletId !== METAMASK_ID) {
                return;
            }
            this.clearSession();
            this.event.trigger({ type: AuthEvent.walletKitLogout });
        });
    }

    getSupportedWallets() {
        return StellarWalletsKit.refreshSupportedWallets();
    }

    startFreighterWatching(publicKey: string) {
        if (!this.watcher) {
            this.watcher = new WatchWalletChanges(1000);
        }
        this.watcher.watch(({ address }) => {
            if (publicKey === address || !address) {
                return;
            }
            this.event.trigger({
                type: AuthEvent.walletKitAccountChanged,
                publicKey: address,
            });
        });
    }

    stopFreighterWatching() {
        this.watcher?.stop();
        this.watcher = null;
    }

    showWalletKitModal() {
        ModalService.closeAllModals();
        ModalService.openModal(WalletKitModal, { modules: this.modules }, false, null, false, () =>
            ModalService.openModal(ChooseLoginMethodModal),
        );
    }

    async login(id: string) {
        if (this.loginPending) {
            return;
        }
        this.loginPending = true;
        try {
            StellarWalletsKit.setWallet(id);

            // v2 getAddress() only reads cached state; fetchAddress() requests wallet access.
            const { address } = await StellarWalletsKit.fetchAddress();
            if (!this.restoreLogin(id, address)) {
                throw new Error('The wallet did not return an account. Please reconnect.');
            }

            this.event.trigger({
                type: AuthEvent.walletKitLogin,
                publicKey: address,
                id,
            });
        } catch (e) {
            ToastService.showErrorToast(parseError(e).message);
        } finally {
            this.loginPending = false;
        }
    }

    restoreLogin(id: string, publicKey: string): boolean {
        if (!publicKey || !this.modules.some(module => module.productId === id)) {
            return false;
        }
        this.stopFreighterWatching();
        StellarWalletsKit.setWallet(id);
        activeAddress.value = publicKey;
        this.connectedWalletId = id;
        this.connectedAddress = publicKey;

        if (id === FREIGHTER_ID) {
            this.startFreighterWatching(publicKey);
        }
        return true;
    }

    private clearSession() {
        this.stopFreighterWatching();
        this.connectedWalletId = null;
        this.connectedAddress = null;
        resetWalletState();
    }

    logout() {
        const module = this.modules.find(module => module.productId === this.connectedWalletId);
        this.clearSession();
        // Kit v2 disconnect() does not await the module's rejection. Handle it explicitly.
        void module?.disconnect?.().catch(e => ToastService.showErrorToast(parseError(e).message));
    }

    async signTx(tx: StellarSdk.Transaction, address: string) {
        if (!this.connectedWalletId) {
            throw new Error('Please reconnect your wallet.');
        }
        StellarWalletsKit.setWallet(this.connectedWalletId);
        try {
            if (this.connectedWalletId === METAMASK_ID) {
                // Wait for MetaMask's asynchronous session restore before the first signature.
                const { address: walletAddress } = await StellarWalletsKit.fetchAddress();
                if (walletAddress !== address || this.connectedWalletId !== METAMASK_ID) {
                    throw new Error('Account changed in MetaMask. Please reconnect your wallet.');
                }
            }
            const xdr = tx.toEnvelope().toXDR('base64');
            const { signedTxXdr } = await StellarWalletsKit.signTransaction(xdr, {
                networkPassphrase: tx.networkPassphrase,
                address,
            });

            if (!signedTxXdr?.trim()) {
                // MetaMask's adapter can discard an error returned in the RPC result.
                throw new Error(
                    this.connectedWalletId === METAMASK_ID
                        ? 'Transaction was not signed in MetaMask. The request may have been cancelled.'
                        : 'The wallet did not return a signed transaction. Please try again.',
                );
            }

            return TransactionBuilder.fromXDR(signedTxXdr, tx.networkPassphrase);
        } catch (e) {
            // SDK modules may reject with plain SEP-43 error objects.
            const error = parseError(e);
            throw new Error(
                error.code === 4001 ? 'Transaction cancelled by the user' : error.message,
            );
        }
    }
}
